// @ts-nocheck
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Trash2, Tag, Tags, Building2, CheckCircle, Percent, X, DollarSign, ToggleLeft, MoreHorizontal, FolderTree } from 'lucide-react';
import { api } from '../../utils/api.js';
import CategoryPicker from './CategoryPicker.tsx';

export default function BulkActionBar({
  selectedIds,
  selectedVariantIds,
  hasPartialVariantSelection,
  onDeselect,
  onDeleted,
  onVariantsDeleted,
  onReverted,
  onVariantDeleteReverted,
  onBulkUpdated,
  onBulkResolved,
  onBulkMarkup,
  onToast,
  products,
  totalCount,
}) {
  const [action, setAction] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [markupType, setMarkupType] = useState('multiplier');
  const [applyPrice, setApplyPrice] = useState(true);
  const [applyCompareAtPrice, setApplyCompareAtPrice] = useState(false);
  const [loading, setLoading] = useState(false);

  const variantIds = selectedVariantIds || [];
  const productIds = selectedIds || [];

  const count = hasPartialVariantSelection ? variantIds.length : productIds.length;

  const effectiveProductIds = hasPartialVariantSelection
    ? [...new Set((products || [])
        .filter(p => (p.variants || []).some(v => variantIds.includes(v.id)))
        .map(p => p.id))]
    : productIds;

  const hasErrorFlags = effectiveProductIds.some(pid => {
    const p = (products || []).find(pr => pr.id === pid);
    return (p?.variants || []).some(v => (v.flags || []).some(f => f.severity === 'error' && !f.resolved));
  });

  function handleDelete() {
    if (hasPartialVariantSelection) {
      onVariantsDeleted?.(variantIds);
    } else {
      onDeleted(productIds);
    }
    setAction(null);
  }

  async function handleBulkUpdate(field, mode) {
    if (!inputVal.trim()) return;
    setLoading(true);
    try {
      await api.post('/products/bulk-update', { product_ids: effectiveProductIds, field, value: inputVal.trim(), mode });
      onBulkUpdated(effectiveProductIds, field, inputVal.trim(), mode);
      setInputVal('');
      setAction(null);
    } catch (e) {
      onToast?.(e.message);
    }
    setLoading(false);
  }

  async function handleBulkResolve() {
    setLoading(true);
    try {
      const scopedVariantIds = hasPartialVariantSelection ? variantIds : null;
      const res = await api.bulkResolveFlags(effectiveProductIds, scopedVariantIds);
      onBulkResolved(res.updated || []);
      setAction(null);
    } catch (e) {
      onToast?.(e.message);
    }
    setLoading(false);
  }

  async function handleSetStatus(value) {
    setLoading(true);
    try {
      await api.post('/products/bulk-update', { product_ids: effectiveProductIds, field: 'status', value });
      onBulkUpdated(effectiveProductIds, 'status', value, 'replace');
      setAction(null);
    } catch (e) {
      onToast?.(e.message);
    }
    setLoading(false);
  }

  async function handleSetPrice() {
    const num = parseFloat(inputVal);
    if (isNaN(num) || num <= 0) return;
    setLoading(true);
    try {
      const scopedVariantIds = hasPartialVariantSelection ? variantIds : null;
      const res = await api.bulkApplyMarkup(effectiveProductIds, 'fixed', num, true, false, scopedVariantIds);
      onBulkMarkup?.(res.variant_prices || []);
      onToast?.(`Price set to $${num.toFixed(2)} on ${res.updated} variant${res.updated !== 1 ? 's' : ''}`);
      setInputVal('');
      setAction(null);
    } catch (e) {
      onToast?.(e.message);
    }
    setLoading(false);
  }

  async function handleApplyMarkup() {
    const num = parseFloat(inputVal);
    if (isNaN(num) || num <= 0) return;
    if (!applyPrice && !applyCompareAtPrice) return;
    setLoading(true);
    try {
      const scopedVariantIds = hasPartialVariantSelection ? variantIds : null;
      const res = await api.bulkApplyMarkup(effectiveProductIds, markupType, num, applyPrice, applyCompareAtPrice, scopedVariantIds);
      onBulkMarkup?.(res.variant_prices || []);
      const skippedMsg = res.skipped > 0 ? ` (${res.skipped} skipped — no price)` : '';
      onToast?.(`Updated ${res.updated} variant${res.updated !== 1 ? 's' : ''}${skippedMsg}`);
      setInputVal('');
      setAction(null);
    } catch (e) {
      onToast?.(e.message);
    }
    setLoading(false);
  }

  /* ── Toolbar button style (neutral grey, matches FilterBar buttons) ── */
  const toolbarBtn = (hovered, isDisabled) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '4px 10px', fontSize: 13, fontWeight: 400,
    background: hovered && !isDisabled ? 'var(--color-surface-2)' : 'transparent',
    border: 'none', borderRadius: 'var(--r-sm)',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    color: 'var(--color-text-2)',
    opacity: isDisabled ? 0.4 : 1,
    transition: 'background 0.12s',
    whiteSpace: 'nowrap',
  });

  const inputStyle = {
    padding: '4px 10px', fontSize: 13, height: 28,
    border: '1px solid var(--color-border)', borderRadius: 'var(--r-sm)',
    background: 'var(--color-surface)', color: 'var(--color-text)',
    outline: 'none',
  };

  const applyBtnStyle = (isDisabled) => ({
    padding: '4px 12px', fontSize: 13, fontWeight: 600, height: 28,
    background: 'var(--color-primary)', color: 'var(--color-surface)',
    border: 'none', borderRadius: 'var(--r-sm)', cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.6 : 1, whiteSpace: 'nowrap',
  });

  const closeBtn = {
    background: 'none', border: 'none', cursor: 'pointer', padding: 4,
    display: 'inline-flex', alignItems: 'center',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.12, ease: 'easeOut' }}
      style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 1,
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        padding: '0 20px',
        display: 'flex', alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', height: 36 }}>

      {/* Selection count chip — click to deselect all */}
      <button
        onClick={onDeselect}
        style={{
          display: 'inline-flex', alignItems: 'center',
          padding: '2px 10px', borderRadius: 'var(--r-sm)',
          background: 'var(--color-primary-light)', color: 'var(--color-primary)',
          fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
          border: 'none', cursor: 'pointer', transition: 'opacity 0.12s',
        }}
        onMouseEnter={e => { e.currentTarget.style.opacity = '0.8'; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
        title="Click to deselect all"
      >
        {count} selected
      </button>

      {!action && (
        <>
          <ToolbarBtn icon={<ToggleLeft size={14} />} label="Set status" onClick={() => setAction('set_status')} />
          <ToolbarBtn icon={<Percent size={14} />} label="Apply markup" onClick={() => setAction('markup')} />
          <span title={hasErrorFlags ? 'Fix errors before marking as reviewed' : undefined}>
            <ToolbarBtn icon={<CheckCircle size={14} />} label="Mark as reviewed" onClick={handleBulkResolve} loading={loading} disabled={hasErrorFlags} />
          </span>
          <ToolbarBtn icon={<Trash2 size={14} />} onClick={handleDelete} loading={loading} title="Delete" />
          <div style={{ position: 'relative' }}>
            <ToolbarBtn
              icon={<MoreHorizontal size={14} />}
              label="More"
              onClick={() => setMoreOpen(o => !o)}
            />
            {moreOpen && (
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 51 }}
                  onClick={() => setMoreOpen(false)}
                />
                <div style={{
                  position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 52,
                  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
                  borderRadius: 8, boxShadow: 'var(--shadow-card)',
                  minWidth: 160, padding: '4px 0', display: 'flex', flexDirection: 'column',
                }}>
                  {[
                    { icon: <Building2 size={14} />, label: 'Set vendor', act: 'vendor' },
                    { icon: <FolderTree size={14} />, label: 'Set category', act: 'set_category' },
                    { icon: <Tag size={14} />, label: 'Set type', act: 'product_type' },
                    { icon: <Tags size={14} />, label: 'Set tags', act: 'tags' },
                    { icon: <DollarSign size={14} />, label: 'Set price', act: 'set_price' },
                  ].map(({ icon, label, act }) => (
                    <button
                      key={act}
                      onClick={() => { setAction(act); setMoreOpen(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '7px 14px', fontSize: 13, fontWeight: 400,
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--color-text)', textAlign: 'left', width: '100%',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-surface-2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                    >
                      {icon}
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {action === 'set_status' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select
            autoFocus
            defaultValue=""
            onChange={e => { if (e.target.value) handleSetStatus(e.target.value); }}
            onKeyDown={e => { if (e.key === 'Escape') setAction(null); }}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            <option value="" disabled>Choose status...</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
          <button onClick={() => setAction(null)} style={closeBtn}>
            <X size={14} color="var(--color-text-3)" />
          </button>
        </div>
      )}

      {action && action !== 'delete' && action !== 'markup' && action !== 'set_price' && action !== 'set_status' && action !== 'set_category' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            autoFocus
            type="text"
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleBulkUpdate(action, 'replace');
              if (e.key === 'Escape') { setAction(null); setInputVal(''); }
            }}
            placeholder={action === 'vendor' ? 'Vendor name...' : action === 'product_type' ? 'Product type...' : 'Tags...'}
            style={{ ...inputStyle, minWidth: 150 }}
          />
          {action === 'tags' && (
            <button onClick={() => handleBulkUpdate('tags', 'add')} disabled={loading} style={applyBtnStyle(loading)}>Add tags</button>
          )}
          <button onClick={() => handleBulkUpdate(action, 'replace')} disabled={loading || !inputVal.trim()} style={applyBtnStyle(loading || !inputVal.trim())}>
            {loading ? '...' : 'Apply'}
          </button>
          <button onClick={() => { setAction(null); setInputVal(''); }} style={closeBtn}>
            <X size={14} color="var(--color-text-3)" />
          </button>
        </div>
      )}

      {action === 'markup' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {['multiplier', 'percent'].map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setMarkupType(t)}
                style={{
                  padding: '3px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', borderRadius: 'var(--r-sm)',
                  border: '1px solid ' + (markupType === t ? 'var(--color-primary)' : 'var(--color-border)'),
                  background: markupType === t ? 'var(--color-primary)' : 'var(--color-surface)',
                  color: markupType === t ? 'var(--color-surface)' : 'var(--color-text-2)',
                }}
              >
                {t === 'multiplier' ? '\u00d7 Multiplier' : '% Markup'}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 2px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--color-text-2)', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={applyPrice}
                onChange={e => setApplyPrice(e.target.checked)}
                style={{ cursor: 'pointer', accentColor: 'var(--color-primary)', width: 14, height: 14 }}
              />
              Price
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--color-text-2)', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={applyCompareAtPrice}
                onChange={e => setApplyCompareAtPrice(e.target.checked)}
                style={{ cursor: 'pointer', accentColor: 'var(--color-primary)', width: 14, height: 14 }}
              />
              Compare At Price
            </label>
          </div>
          <input
            autoFocus
            type="number"
            min="0"
            step={markupType === 'multiplier' ? '0.1' : '1'}
            placeholder={markupType === 'multiplier' ? 'e.g. 2.5' : 'e.g. 150'}
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleApplyMarkup();
              if (e.key === 'Escape') { setAction(null); setInputVal(''); }
            }}
            style={{ ...inputStyle, width: 90 }}
          />
          {inputVal && parseFloat(inputVal) > 0 && (() => {
            const samplePrice = (products || []).reduce((found, p) => {
              if (found) return found;
              const pr = p.variants?.[0]?.price;
              return (pr && Number(pr) > 0) ? Number(pr) : null;
            }, null) ?? 12.00;
            const newPrice = markupType === 'multiplier'
              ? samplePrice * parseFloat(inputVal)
              : samplePrice * (1 + parseFloat(inputVal) / 100);
            return (
              <span style={{ fontSize: 11, color: 'var(--color-primary)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                e.g. ${samplePrice.toFixed(2)} &rarr; ${newPrice.toFixed(2)}
              </span>
            );
          })()}
          <button
            onClick={handleApplyMarkup}
            disabled={loading || !inputVal || parseFloat(inputVal) <= 0 || (!applyPrice && !applyCompareAtPrice)}
            style={applyBtnStyle(loading || !inputVal || parseFloat(inputVal) <= 0 || (!applyPrice && !applyCompareAtPrice))}
          >
            {loading ? '...' : `Apply to ${count}`}
          </button>
          <button onClick={() => { setAction(null); setInputVal(''); }} style={closeBtn}>
            <X size={14} color="var(--color-text-3)" />
          </button>
        </div>
      )}

      {action === 'set_category' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 280 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <CategoryPicker
              value=""
              categoryId=""
              onSelect={async (gid, name) => {
                setLoading(true);
                try {
                  await api.post('/products/bulk-update', { product_ids: effectiveProductIds, field: 'product_category_id', value: gid });
                  onBulkUpdated(effectiveProductIds, 'product_category', name, 'replace');
                  setAction(null);
                } catch (e) {
                  onToast?.(e.message);
                }
                setLoading(false);
              }}
              onClear={() => setAction(null)}
              placeholder="Search categories..."
            />
          </div>
          <button onClick={() => setAction(null)} style={closeBtn}>
            <X size={14} color="var(--color-text-3)" />
          </button>
        </div>
      )}

      {action === 'set_price' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--color-text-2)', whiteSpace: 'nowrap' }}>
            Set all to
          </span>
          <input
            autoFocus
            type="number"
            min="0"
            step="0.01"
            placeholder="e.g. 29.99"
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSetPrice();
              if (e.key === 'Escape') { setAction(null); setInputVal(''); }
            }}
            style={{ ...inputStyle, width: 100 }}
          />
          <button
            onClick={handleSetPrice}
            disabled={loading || !inputVal || parseFloat(inputVal) <= 0}
            style={applyBtnStyle(loading || !inputVal || parseFloat(inputVal) <= 0)}
          >
            {loading ? '...' : `Apply to ${count}`}
          </button>
          <button onClick={() => { setAction(null); setInputVal(''); }} style={closeBtn}>
            <X size={14} color="var(--color-text-3)" />
          </button>
        </div>
      )}

      </div>
    </motion.div>
  );
}

function ToolbarBtn({ icon, label, onClick, loading, disabled, title }) {
  const isDisabled = loading || disabled;
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: label ? '4px 10px' : '4px 6px', fontSize: 13, fontWeight: 400,
        background: 'transparent',
        border: 'none', borderRadius: 'var(--r-sm)',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        color: 'var(--color-text-2)',
        opacity: isDisabled ? 0.4 : 1,
        transition: 'background 0.12s',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => { if (!isDisabled) e.currentTarget.style.background = 'var(--color-surface-2)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      {icon}
      {label}
    </button>
  );
}
